<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            :header-height="50"
            grid-theme="ag-theme-alpine-report"
            class="discounts-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div
                class="
                    d-none d-lg-flex
                    align-items-baseline
                    mr-1
                    mb-1
                    filter-properties
                "
            >
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <b-button-group
                v-if="hasMultiProps"
                class="d-none d-lg-block show-roomtype mr-1"
            >
                <form-square-switch
                    v-model="showRoomtype"
                    @change="onShowRoomtypeChange"
                    :labels="{
                        checked: 'Show Roomtype',
                        unchecked: 'Show Roomtype',
                    }"
                    :is-toolbar="true"
                ></form-square-switch>
            </b-button-group>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>

            <b-button-group class="d-lg-none mr-1">
                <more-filters-dropdown>
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <form-search-multiselect
                            v-model="filterProperties"
                            :options="filterPropertyOptions"
                            :icon="['fas', 'hotel']"
                            variant="primary"
                            placeholder="Filter Properties"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <form-square-switch
                            v-if="hasMultiProps"
                            v-model="showRoomtype"
                            @change="onShowRoomtypeChange"
                            :labels="{
                                checked: 'Show Roomtype',
                                unchecked: 'Show Roomtype',
                            }"
                            :is-toolbar="true"
                        ></form-square-switch>
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
                :excelOnly="true"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    LAST_WEEK,
    LAST_YEAR,
} from '../generic/date-range-picker/date-range-picker.vue';
import {
    REPORT_TAX_ENDPOINT,
    REPORT_TAX_PROPERTIES_ENDPOINT,
} from '../../endpoints';
import ReportSummaryBar from './report-summary-bar.vue';
import { toAmountDisplay } from '../../utils/numbers';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { isMediumScreen } from '../../utils/screens';
import ReportSettingsModal, {
    MULTI_SELECT_INPUT,
    DATE_PERIODS_INPUT,
    SWITCH_INPUT,
} from './report-settings/report-settings-modal.vue';

export default {
    name: 'SalesUseTaxReport',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        DateRangePicker,
        FormSearchMultiselect,
        FormSquareSwitch,
        reportSummaryBar: ReportSummaryBar,
        ExportToExcelButton,
        MoreFiltersDropdown,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Sales / Use Tax Report',
            tabs: [
                {
                    title: 'Sales / Use Tax Report',
                    route: '/app/reports/tax/',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_MONTH),
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                LAST_WEEK,
                THIS_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                THIS_YEAR,
                LAST_YEAR,
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            gridApi: null,
            rowData: [],
            taxesList: [],
            totals: [],
            getRowStyle: null,
            windowWidth: 0,
            windowHeight: 0,
            filterProperties: [],
            filterPropertyOptions: [],
            showRoomtype: false,
            statusBar: null,
            hasMultiProps: false,
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    headerName: '#',
                    valueGetter: (params) => {
                        return params.node.rowPinned
                            ? this.rowData.length
                            : params.node.rowIndex + 1;
                    },
                    minWidth: 40,
                    pinned: 'left',
                },
                {
                    field: 'pTitle',
                    headerName: 'Property/Room',
                    suppressSizeToFit: true,
                    pinned: 'left',
                    width: isMediumScreen() ? undefined : 80,
                    cellStyle: (params) => {
                        if (!params.data.isRoomType) {
                            return { 'font-weight': 'bold' };
                        }
                        return null;
                    },
                    autoWidthExclude: true,
                },
                ...this.taxesList.map((tax) => {
                    return {
                        headerName: tax.title,
                        field: tax.tax_name_url,
                        type: 'amountColumn',
                        minWidth: 180,
                        comparator: (valueA, valueB) => valueA - valueB,
                        cellStyle: (params) => {
                            if (!params.data.isRoomType) {
                                return { 'font-weight': 'bold' };
                            }
                            return null;
                        },
                        cellRenderer: (params) => {
                            const { data, node, value } = params;
                            const amountValue = toAmountDisplay({
                                amount: value,
                                currency: this.userCurrencySymbol,
                            });
                            if (!node.rowPinned) {
                                return `<a target="_self" href="/app/reports/guest-register/?from_date=${this.startDateRequest}&to_date=${this.endDateRequest}&pk=${data.pId}&tax_name=${tax.tax_name_url}">${amountValue}</a>`;
                            }
                            return amountValue;
                        },
                    };
                }),
                {
                    field: 'total',
                    headerName: 'Total',
                    type: 'amountColumn',
                    minWidth: 180,
                    comparator: (valueA, valueB) => valueA - valueB,
                    autoWidthExclude: true,
                },
            ];
        },
        pinnedBottomRowData() {
            if (this.taxesList.length > 0 && this.totals.length > 0) {
                const bottomRow = {};
                this.taxesList.forEach((tax, index) => {
                    bottomRow[tax.tax_name_url] = this.totals[index];
                });
                bottomRow['total'] = this.totals[this.taxesList.length];
                return [
                    {
                        ...bottomRow,
                    },
                ];
            }
            return [{}];
        },
        exportExcelHref() {
            let url = '/app/reports/tax/row-data/excel/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            url += `&show_roomtype=${this.showRoomtype}`;
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        isMobile() {
            const md = 768;
            return this.windowWidth < md || this.windowHeight < md;
        },
        reportSettings() {
            const settings = {
                name: 'Tax',
                report_sysname: 'TAX',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        predefinedRanges: this.predefinedRanges,
                        value: 'this_month',
                        label: 'Date Period',
                        name: 'from_date',
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        value: [],
                        label: 'Only for Properties',
                        name: 'properties',
                        options: this.filterPropertyOptions,
                        placeholder: 'All Properties',
                    },
                ],
            };
            if (this.hasMultiProps) {
                settings.settings.push({
                    type: SWITCH_INPUT,
                    value: false,
                    label: 'Show Roomtype',
                    name: 'show_roomtype',
                });
            }
            return settings;
        },
    },
    mounted() {
        this.fetchProperties();
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(REPORT_TAX_ENDPOINT(), {
                    params: {
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        properties: this.filterProperties.join(','),
                        show_roomtype: this.showRoomtype,
                    },
                });
                const { taxes_list: taxesList, rows, totals } = data;
                this.rowData = rows.map((r, index) => {
                    const { property, taxes, total, room_type: isRoomType } = r;
                    const row = {
                        id: index,
                        pId: property.id,
                        pTitle: property.name,
                        total,
                        isRoomType,
                    };
                    taxes.forEach((tax) => {
                        const { tax_name_url: taxField, value } = tax;
                        row[taxField] = value;
                    });
                    return row;
                });
                this.taxesList = taxesList;
                this.totals = totals;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            const { data } = await API.get(REPORT_TAX_PROPERTIES_ENDPOINT());
            const { properties, has_multi_props: hasMultiProps } = data;
            this.hasMultiProps = hasMultiProps;
            if (!this.hasMultiProps) {
                this.showRoomtype = false;
            }
            this.filterPropertyOptions = properties.map((item) => ({
                value: item.property_id,
                text: item.name,
                disabled: item.disabled,
            }));
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent =
                this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
        onShowRoomtypeChange() {
            this.fetchData();
        },
    },
    watch: {
        filterProperties: {
            deep: true,
            handler() {
                this.fetchData();
            },
        },
    },
};
</script>

<style lang="scss">
.discounts-report {
    .filter-properties {
        > fieldset {
            margin: 0 !important;
        }
    }
    .show-roomtype {
        width: 140px;
    }
}
</style>